import React from 'react';
import { equals } from 'ramda';
import { useDatasourceId, useSeriesId } from '@/hooks';
import { useModalClose } from '@/features/modals/modalSlice';
import {
  useGetSavedQuery,
  usePatchSavedQueryMutation,
} from '@/features/query-builder/savedQueriesApiSlice';
import {
  Modal,
  InputGroup,
  InputGroupTagifyPatch,
} from '@/features/modals/components';
import { Button } from '@trevor/ui-components';
import { CategoriesInput } from '@/views/QueryBuilder/subcomponents/TopControls/CategoriesInput';
import { usePrefetchCategories } from '@/features/categories/categoriesApiSlice';

export const QueryCategoriesSection: React.FC<any> = ({
  categories = [],
  onUpdate,
  initiallyOpen = false,
}) => {
  const prefetchCategories = usePrefetchCategories();
  const [isCategoriesInputOpen, setIsCategoriesInputOpen] =
    React.useState(initiallyOpen);

  return (
    <InputGroupTagifyPatch>
      <label id="trevor-query-categories">Categorize this query</label>
      {isCategoriesInputOpen ? (
        <CategoriesInput
          categories={categories}
          onUpdateCategories={onUpdate}
        />
      ) : (
        <Button
          variant="secondary"
          size="small"
          onClick={() => setIsCategoriesInputOpen(true)}
          onMouseEnter={prefetchCategories}
        >
          Add category
        </Button>
      )}
    </InputGroupTagifyPatch>
  );
};

export const useCategoriesSelect = (initialCategories = []) => {
  const [categories, setCategories] = React.useState<any[]>(initialCategories);
  const handleUpdateCategories = (method: 'add' | 'remove', tagData: any) => {
    if (method === 'add') {
      setCategories((cats: any) => [
        ...cats,
        tagData.id
          ? {
              _id: tagData.id,
              _name: tagData.value,
              _count: tagData.count,
            }
          : { _name: tagData.value },
      ]);
    }
    if (method === 'remove') {
      setCategories((cats: any) =>
        cats.filter((c: any) => c._name !== tagData.value),
      );
    }
  };

  return [categories, handleUpdateCategories] as const;
};

export const ModalUpdateSavedQuery = () => {
  const datasourceId = useDatasourceId();
  const seriesId = useSeriesId();
  const handleClose = useModalClose();
  const nameInputRef = React.useRef<HTMLInputElement>(null);
  const [updateSavedQuery, { isLoading, isSuccess }] =
    usePatchSavedQueryMutation();

  React.useEffect(() => {
    if (isSuccess) handleClose();
  }, [isSuccess]);

  const { data } = useGetSavedQuery();

  const currSavedQueryNameName = data?.name ?? '';
  const currSavedQueryCategories = data?.categories ?? [];
  const [queryName, setQueryName] = React.useState(currSavedQueryNameName);
  const [categories, setCategories] = useCategoriesSelect(
    currSavedQueryCategories,
  );

  React.useEffect(() => {
    nameInputRef.current?.select();
  }, []);

  const categoriesUpdated = !equals(currSavedQueryCategories, categories);

  const hasUpdates =
    currSavedQueryNameName !== queryName.trim() || categoriesUpdated;
  const handleUpdate = () =>
    updateSavedQuery({
      datasourceId,
      seriesId,
      updatedSavedQuery: categoriesUpdated
        ? { name: queryName, categories }
        : { name: queryName },
    });

  return (
    <Modal
      title="Update saved query"
      onSubmit={handleUpdate}
      isCancelDisabled={isLoading}
      isSubmitDisabled={!hasUpdates || isLoading}
    >
      <InputGroup>
        <label htmlFor="trevor-query-name">Name your query</label>
        <input
          type="text"
          id="trevor-query-name"
          ref={nameInputRef}
          value={queryName}
          onChange={(e) => setQueryName(e.target.value)}
        />
      </InputGroup>
      <QueryCategoriesSection
        categories={currSavedQueryCategories}
        onUpdate={setCategories}
        initiallyOpen={categories.length > 0}
      />
    </Modal>
  );
};
